const DATE_FORMAT_OPTIONS = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric'
};
const LIMA_TIME_ZONE = 'America/Lima';
const DATE_ONLY_REGEX = /^(\d{4}-\d{2}-\d{2})/;

const DATE_TIME_FORMAT_OPTIONS = {
  ...DATE_FORMAT_OPTIONS,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: LIMA_TIME_ZONE
};

const MONEY_FORMATTER = new Intl.NumberFormat('es-PE', {
  style: 'currency',
  currency: 'PEN',
  minimumFractionDigits: 2
});

const DECIMAL_AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;

const resolvePrestamoId = (prestamo) => {
  if (prestamo && typeof prestamo === 'object') {
    return prestamo.id_prestamo ?? '';
  }

  return prestamo ?? '';
};

const formatearFechaISOEnLima = (fecha = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: LIMA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(fecha);

  const dateParts = parts.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }

    return acc;
  }, {});

  return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
};

const normalizarFechaISO = (fecha) => {
  if (!fecha) {
    return '';
  }

  const rawValue = String(fecha).trim();
  const dateOnlyMatch = rawValue.match(DATE_ONLY_REGEX);
  if (dateOnlyMatch) {
    return dateOnlyMatch[1];
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return formatearFechaISOEnLima(parsedDate);
};

export const obtenerFechaActualISO = () => formatearFechaISOEnLima();

export const formatearFecha = (fecha) => {
  if (!fecha) {
    return '-';
  }

  const rawValue = String(fecha);
  const parsedDate = rawValue.includes('T') ? new Date(rawValue) : new Date(`${rawValue}T00:00:00`);

  return parsedDate.toLocaleDateString('es-PE', DATE_FORMAT_OPTIONS);
};

export const formatearFechaHora = (fecha) => {
  if (!fecha) {
    return '-';
  }

  return new Date(fecha).toLocaleString('es-PE', DATE_TIME_FORMAT_OPTIONS);
};

export const formatearMoneda = (monto) => MONEY_FORMATTER.format(Number(monto) || 0);

export const crearFormularioCompromiso = (prestamo = null, compromiso = null) => ({
  idPrestamo: resolvePrestamoId(prestamo),
  idCompromiso: compromiso?.id ?? null,
  fecha_compromiso: compromiso?.fecha_compromiso ?? '',
  monto_compromiso: String(compromiso?.monto_compromiso ?? ''),
  motivo: compromiso?.motivo ?? ''
});

export const obtenerRestriccionNuevoCompromiso = (prestamo, fechaActual = formatearFechaISOEnLima()) => {
  const fechaUltimoCompromiso = normalizarFechaISO(prestamo?.ultimo_compromiso_pago?.fecha_compromiso);

  if (!fechaUltimoCompromiso || fechaUltimoCompromiso <= fechaActual) {
    return {
      bloqueado: false,
      fechaDisponible: null,
      mensaje: '',
      ayuda: ''
    };
  }

  const fechaFormateada = formatearFecha(fechaUltimoCompromiso);

  return {
    bloqueado: true,
    fechaDisponible: fechaUltimoCompromiso,
    mensaje: `Ya existe un compromiso vigente hasta el ${fechaFormateada}.`,
    ayuda: `Disponible desde ${fechaFormateada}`
  };
};

export const validarCompromisoForm = (formData, { minFechaCompromiso = '' } = {}) => {
  const nextErrors = {};
  const fechaCompromiso = normalizarFechaISO(formData.fecha_compromiso);
  const fechaMinimaCompromiso = normalizarFechaISO(minFechaCompromiso);

  if (!fechaCompromiso) {
    nextErrors.fecha_compromiso = 'La fecha de compromiso es obligatoria.';
  } else if (fechaMinimaCompromiso && fechaCompromiso < fechaMinimaCompromiso) {
    nextErrors.fecha_compromiso = 'La fecha de compromiso no puede ser menor a hoy.';
  }

  const montoCompromiso = String(formData.monto_compromiso || '').trim();
  if (!montoCompromiso) {
    nextErrors.monto_compromiso = 'El monto del compromiso es obligatorio.';
  } else if (!DECIMAL_AMOUNT_REGEX.test(montoCompromiso)) {
    nextErrors.monto_compromiso = 'El monto del compromiso debe ser numérico y tener hasta 2 decimales.';
  }

  if (!String(formData.motivo || '').trim()) {
    nextErrors.motivo = 'El motivo es obligatorio.';
  }

  return nextErrors;
};

export const getNivelCriticidad = (diasMora = 0) => {
  if (diasMora <= 7) {
    return { badge: 'bg-amber-100 text-amber-800' };
  }

  if (diasMora <= 30) {
    return { badge: 'bg-orange-100 text-orange-800' };
  }

  return { badge: 'bg-red-100 text-red-800' };
};